'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { myPlannerApi } from './my-planner.api';
import type { PlannerCounselSlotResponse } from './planner-counsel-slot.api';

const VALID_STATUSES = new Set(['APPOINTED', 'AVAILABLE']);

function hasWorkSlot(responses: PlannerCounselSlotResponse[]): boolean {
  return responses.some((r) => r.slots?.some((s) => VALID_STATUSES.has(s.status)) ?? false);
}

async function fetchMyTodayWorkStatus(today: string): Promise<'work' | 'off'> {
  const responses = await myPlannerApi.getMySlotsByDate(today);
  return hasWorkSlot(responses) ? 'work' : 'off';
}

/**
 * PLANNER 전용: "오늘" 한 날짜의 근무 여부만 빠르게 조회.
 *
 * `useMyPlannerWorkScheduleDates` 는 ±90일 범위를 한 번에 받아오기 때문에
 * 첫 마운트 시 응답이 늦어 캘린더에 휴무 마킹이 한참 비어 보이는 문제가 있다.
 * 본 훅은 단일-일 endpoint 를 사용해 오늘의 상태만 즉시 알아내, 캘린더가
 * 첫 페인트 시점부터 최소한 오늘에 대한 휴무/근무 표시를 보여줄 수 있게 한다.
 *
 * SWR 키는 오늘 날짜로만 잡혀 있어 selectedDate 가 바뀌어도 재요청되지 않는다.
 */
export function useMyPlannerTodayWorkStatus(enabled = true): {
  todayDateString: string;
  todayWorkStatus: 'work' | 'off' | null;
  isLoading: boolean;
} {
  const todayDateString = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const { data, isLoading } = useSWR<'work' | 'off'>(
    enabled ? ['my-planner-today-work-status', todayDateString] : null,
    () => fetchMyTodayWorkStatus(todayDateString)
  );

  return {
    todayDateString,
    todayWorkStatus: data ?? null,
    isLoading,
  };
}
